import React, { Component } from 'react';
import _ from 'lodash';
import {
  shape,
  arrayOf,
  string,
  func,
} from 'prop-types';
import { addEventListener } from 'consolidated-events';
import { AnchorTabs, Freeze } from 'components/common';
import { getObjectFromArray } from 'pages/Verticals/ResultsPage/ResultsPageUtils';
import { withTracking, contentGroupingsTrack } from 'components/tracking';
import { getProductLastUpdateDateFormat } from 'helpers/DateFormatUtil';
import { startAnimation } from 'components/common/helpers/animation';
import { setApplicationFormRetryUrl } from 'helpers/Utils';
import { withRouter } from 'react-router';
import SupportButtonStateContainer from 'containers/SupportButtonStateContainer';
import withSubscribe from 'containers/withSubscribe';
import { FaqPanel } from '@hyphengroup/cag-components';
import ProductHeroPanel from 'components/product/ProductHeroPanel';
import StickyCTABanner from 'components/product/StickyCTABanner';
import BenefitsSection from 'pages/Verticals/ProductPage/BenefitsSection';
import EligibilitySection from 'pages/Verticals/ProductPage/EligibilitySection';
import AprFeesSection from 'pages/Verticals/ProductPage/AprFeesSection';
import FeaturesSection from 'pages/Verticals/ProductPage/FeaturesSection';
import CompareOtherProductsPanel from 'pages/Verticals/ProductPage/CompareOtherProductsPanel';
import {
  onApplyTrack,
  productPageLoadTrack,
} from 'pages/Verticals/ProductPage/ProductPageTrackingUtils';

import OffersSection from './OffersSectionTW';
import './ProductPage.scss';
import SeoTable from '../../../../../components/seo/SeoTable';

export const staticTextKeys = [
  'CC_PRODUCT_PAGE_OFFERS_TITLE',
  'CC_PRODUCT_PAGE_OFFERS_OPTION_LABEL',
  'CC_PRODUCT_PAGE_OFFERS_EXCLUSIVE_OFFERS_LABEL',
  'CC_PRODUCT_PAGE_OFFERS_WELCOME_OFFERS_LABEL',
  'CC_PRODUCT_PAGE_OFFERS_EXCLUSIVE_LABEL',
  'CC_PRODUCT_PAGE_FEATURES_GREAT_FOR_LABEL',
  'CC_PRODUCT_PAGE_FEATURES_NEED_TO_KNOW_LABEL',
  'CC_PRODUCT_PAGE_BENEFITS_TITLE',
  'CC_PRODUCT_PAGE_BENEFITS_YEAR_ROUND_REWARDS_LABEL',
  'CC_PRODUCT_PAGE_BENEFITS_YEAR_ROUND_BENEFITS_LABEL',
  'CC_PRODUCT_PAGE_BENEFITS_AIR_MILES_REWARDS_LABEL',
  'CC_PRODUCT_PAGE_BENEFITS_CASH_REBATE_LABEL',
  'CC_PRODUCT_PAGE_BENEFITS_OFFER_LABEL',
  'CC_PRODUCT_PAGE_BENEFITS_NA_VALUE',
  'CC_PRODUCT_PAGE_BENEFITS_CASH_REBATE_SUFFIX',
  'CC_PRODUCT_PAGE_BENEFITS_AIR_MILES_PREFIX',
  'CC_PRODUCT_PAGE_BENEFITS_AIR_MILES_SUFFIX',
  'showMoreLabel',
  'showLessLabel',
  'CC_PRODUCT_PAGE_ELIGIBILTY_TITLE',
  'CC_PRODUCT_PAGE_APR_FEES_TITLE',
  'CC_PRODUCT_PAGE_APR_FEES_SUB_TITLE',
  'CC_PRODUCT_PAGE_APR_FEES_FEES_LABEL',
  'CC_PRODUCT_PAGE_APR_FEES_CANCELLATION_LABEL',
  'CC_PRODUCT_PAGE_APR_FEES_OTHER_LABEL',
  'CC_PRODUCT_PAGE_APR_FEES_PAYMENT_NETWORK_LABEL',
  'CC_PRODUCT_PAGE_APR_FEES_SUPPORTED_PAYMENT_TYPES_LABEL',
  'CC_PRODUCT_PAGE_UPDATED_AT_LABEL',
  'CTA_LABEL_DEFAULT',
  'CC_PRODUCT_PAGE_COMPARE_TITLE',
  'CC_PRODUCT_PAGE_COMPARE_SUB_TITLE',
  'CC_PRODUCT_PAGE_COMPARE_COMPARE_LABEL',
];

class ProductPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ctaBannerHidden: true,
      isSupportButtonLoaded: false,
    };

    this.handleScroll = this.handleScroll.bind(this);
    this.setCtaRef = this.setCtaRef.bind(this);
    this.onApplyButtonClick = this.onApplyButtonClick.bind(this);
  }

  static getDerivedStateFromProps(nextProps) {
    const { subscribedContainers: [supportButtonState] } = nextProps;
    const { isLoaded } = supportButtonState.state;
    return {
      isSupportButtonLoaded: isLoaded,
    };
  }

  componentDidMount() {
    this.removeScrollListener = addEventListener(window, 'scroll', this.handleScroll);
    /* Tracking */
    const { track } = this.props;
    contentGroupingsTrack(track, 'product');
    productPageLoadTrack(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { prevIsSupportButtonLoaded } = prevState;
    const { ctaBannerHidden, isSupportButtonLoaded } = this.state;
    const zenDeskBtn = window.zE;

    if (zenDeskBtn) {
      if (ctaBannerHidden === prevState.ctaBannerHidden
        && prevIsSupportButtonLoaded === isSupportButtonLoaded) return;
      zenDeskBtn('webWidget', 'updateSettings', {
        webWidget: {
          offset: {
            vertical: !ctaBannerHidden ? '100px' : '0px',
            mobile: {
              vertical: !ctaBannerHidden ? '100px' : '0px',
            },
          },
        },
      });
    }
  }

  componentWillUnmount() {
    this.removeScrollListener();
    // to reset the button to the bottom right
    if (window.zE) {
      window.zE('webWidget', 'updateSettings', {
        webWidget: {
          offset: {
            vertical: '0px',
            mobile: {
              vertical: '0px',
            },
          },
        },
      });
    }
  }

  onApplyButtonClick(intent) {
    const { product, location } = this.props;

    // Store url for application form try again url
    setApplicationFormRetryUrl(`${location.pathname}${location.search}`);

    onApplyTrack(this.props, {
      intent,
      product,
    });
  }

  // Get the CTA button in product hero panel, in order to decide whether we show cta banner or not
  setCtaRef(ref) {
    this.ctaRef = ref;
  }

  getRewards(offers) {
    const result = {};

    result.exclusiveOffers = _.get(offers, 'exclusiveOffers') || [];
    if (result.exclusiveOffers.length) {
      result.exclusiveOffers = result.exclusiveOffers[0].reward;
    }
    result.welcomeOffers = _.get(offers, 'welcomeOffers') || [];
    if (result.welcomeOffers.length) {
      result.welcomeOffers = result.welcomeOffers[0].reward;
    }
    result.yearRoundOffers = _.get(offers, 'yearRoundOffers') || [];
    if (result.yearRoundOffers.length) {
      result.yearRoundOffers = result.yearRoundOffers[0].reward;
    }
    result.additionalOffers = _.get(offers, 'additionalOffers') || [];
    if (result.additionalOffers.length) {
      result.additionalOffers = result.additionalOffers[0].reward;
    }

    return result;
  }

  shouldDisplay = (...props) => props.some(prop => prop && prop.length > 0);

  getActiveTabs = (
    exclusiveRewards,
    welcomeRewards,
    yearRoundOffers,
    additionalOffers,
    eligibilityCriteria,
    fees,
    apr,
    cardCancellationInfo,
    otherInfo,
    staticT,
  ) => {
    const tabs = [];

    if (this.shouldDisplay(exclusiveRewards, welcomeRewards)) {
      tabs.push(staticT.CC_PRODUCT_PAGE_OFFERS_TITLE);
    }
    if (this.shouldDisplay(yearRoundOffers, additionalOffers)) {
      tabs.push(staticT.CC_PRODUCT_PAGE_BENEFITS_TITLE);
    }
    if (this.shouldDisplay(eligibilityCriteria)) {
      tabs.push(staticT.CC_PRODUCT_PAGE_ELIGIBILTY_TITLE);
    }
    if (this.shouldDisplay(fees, apr, cardCancellationInfo, otherInfo)) {
      tabs.push(staticT.CC_PRODUCT_PAGE_APR_FEES_TITLE);
    }

    return tabs;
  };

  handleScroll() {
    const { product } = this.props;

    if (product && product.ctaButton && product.ctaButton.show && this.ctaRef) {
      startAnimation(() => {
        const ctaBannerHidden = (window.screenY < this.ctaRef.getBoundingClientRect().bottom);
        this.setState(prevState => ((prevState.ctaBannerHidden !== ctaBannerHidden)
          ? { ctaBannerHidden }
          : null
        ));
      });
    }
  }

  render() {
    const {
      product = {},
      categories,
      categoryRouteTemplate,
      language,
      locale,
      staticPageText: {
        staticText,
      } = {},
      seoTable,
      vertical,
    } = this.props;
    const staticT = getObjectFromArray(staticText);
    const { ctaBannerHidden } = this.state;
    const {
      features = {},
      needCategories,
      description: {
        shortDescription = [],
      } = {},
      fees,
      policy,
      paymentNetwork,
      apr,
      cardCancellationInfo,
      otherInfo,
      eligibilityCriteria,
      offers = {},
      generalFaq = {},
      offersFaq = {},
      benefitsFaq = {},
      updatedAt,
      promotional,
      provider,
      productName,
    } = product || {};
    const {
      exclusiveOffers,
      welcomeOffers,
      yearRoundOffers,
      additionalOffers,
    } = this.getRewards(offers);

    const activeTabs = this.getActiveTabs(
      exclusiveOffers,
      welcomeOffers,
      yearRoundOffers,
      additionalOffers,
      eligibilityCriteria,
      fees,
      apr,
      cardCancellationInfo,
      otherInfo,
      staticT
    );

    const formattedUpdatedAt = getProductLastUpdateDateFormat(updatedAt, locale);
    const updatedAtText = `${staticT.CC_PRODUCT_PAGE_UPDATED_AT_LABEL}${formattedUpdatedAt}`;
    const ctaButton = {
      ...product && product.ctaButton,
      label: _.get(product, 'ctaButton.label') || staticT.CTA_LABEL_DEFAULT, // 'Apply now',
    };

    return (
      <div className="product-page" data-selector-type="product-page">
        <ProductHeroPanel
          {...product}
          ctaButton={ctaButton}
          bgImageUrl="https://images.contentstack.io/v3/assets/bltdf909986be592dae/bltf5d8671fdc30a450/5cca8d6765b2c4dc653fbf32/img_credit_card_bg.png"
          setCtaRef={this.setCtaRef}
          updatedAtText={updatedAtText}
          onCtaLinkClick={() => this.onApplyButtonClick('apply')}
        />
        <AnchorTabs
          tabs={activeTabs}
        >
          {this.shouldDisplay(exclusiveOffers, welcomeOffers) && (
            <OffersSection
              exclusiveOffers={exclusiveOffers}
              welcomeOffers={welcomeOffers}
              faqs={offersFaq}
              isTabPanel={true}
              labels={{
                heading: staticT.CC_PRODUCT_PAGE_OFFERS_TITLE, // Offers
                exclusiveOffers: // 'from MoneyHero.com.hk',
                  staticT.CC_PRODUCT_PAGE_OFFERS_EXCLUSIVE_OFFERS_LABEL,
                welcomeOffers: // 'Welcome Offers from ${provider.providerName}',
                  staticT.CC_PRODUCT_PAGE_OFFERS_WELCOME_OFFERS_LABEL,
                exclusive: // 'EXCLUSIVE'
                  staticT.CC_PRODUCT_PAGE_OFFERS_EXCLUSIVE_LABEL,
                optionLabel: staticT.CC_PRODUCT_PAGE_OFFERS_OPTION_LABEL, // Option
                loadingText: staticT.RESULTS_PAGE_PROMOTIONAL_LABEL_LOADING_TEXT,
                endText: staticT.RESULTS_PAGE_PROMOTIONAL_LABEL_END_TEXT,
              }}
              provider={provider}
              onCtaLinkClick={() => this.onApplyButtonClick('apply')}
              ctaButton={ctaButton}
              productName={productName}
              promotional={promotional}
            />
          )}
          {(this.shouldDisplay(features.pros)
            || this.shouldDisplay(features.cons)
            || this.shouldDisplay(needCategories)) && (
            <FeaturesSection
              titles={shortDescription || []}
              features={features}
              needCategories={needCategories}
              labels={{
                subHeadingGreatFor: // 'Great for',
                  staticT.CC_PRODUCT_PAGE_FEATURES_GREAT_FOR_LABEL,
                subHeadingNeedToKnow: // 'Need to know',
                  staticT.CC_PRODUCT_PAGE_FEATURES_NEED_TO_KNOW_LABEL,
              }}
            />
          )}
          {this.shouldDisplay(yearRoundOffers, additionalOffers) && (
            <BenefitsSection
              yearRoundOffers={yearRoundOffers}
              additionalOffers={additionalOffers}
              faqs={benefitsFaq}
              isTabPanel={true}
              labels={{
                heading: staticT.CC_PRODUCT_PAGE_BENEFITS_TITLE, // Benefits
                yearRoundRewards: // 'Year-round rewards',
                  staticT.CC_PRODUCT_PAGE_BENEFITS_YEAR_ROUND_REWARDS_LABEL,
                yearRoundBenefits: // 'Year-round benefits',
                  staticT.CC_PRODUCT_PAGE_BENEFITS_YEAR_ROUND_BENEFITS_LABEL,
                CASH_REBATE_REWARD:
                  staticT.CC_PRODUCT_PAGE_BENEFITS_CASH_REBATE_LABEL, // AIR MILES REWARD
                AIR_MILES_REWARD:
                  staticT.CC_PRODUCT_PAGE_BENEFITS_AIR_MILES_REWARDS_LABEL, // CASH REBATE
                notApplicableValue: // 'N/A',
                  staticT.CC_PRODUCT_PAGE_BENEFITS_NA_VALUE, // TODO: maybe not the right place?
                cashRebateReward: {
                  suffix: // '%', TODO: Maybe not a static val?
                    staticT.CC_PRODUCT_PAGE_BENEFITS_CASH_REBATE_SUFFIX,
                },
                airMilesReward: {
                  prefix: // '$', TODO: maybe not a static value
                    staticT.CC_PRODUCT_PAGE_BENEFITS_AIR_MILES_PREFIX,
                  suffix: // '/miles', TODO maybe not a static value
                    staticT.CC_PRODUCT_PAGE_BENEFITS_AIR_MILES_SUFFIX,
                },
                toggleLabels: {
                  showMore: staticT.showMoreLabel,
                  showLess: staticT.showLessLabel,
                },
                offerLabel: staticT.CC_PRODUCT_PAGE_BENEFITS_OFFER_LABEL,
                showMoreThreshold: 6, // TODO store somewhere
              }}
            />
          )}
          {this.shouldDisplay(eligibilityCriteria) && (
            <EligibilitySection
              eligibilityCriteria={eligibilityCriteria}
              isTabPanel={true}
              labels={{
                heading: staticT.CC_PRODUCT_PAGE_ELIGIBILTY_TITLE, // Eligibility
                toggleLabels: {
                  showMore: staticT.showMoreLabel,
                  showLess: staticT.showLessLabel,
                },
                showMoreThreshold: 3, // TODO store somewhere
              }}
            />
          )}
          {this.shouldDisplay(fees, apr, cardCancellationInfo, otherInfo) && (
            <AprFeesSection
              fees={fees}
              apr={apr}
              cardCancelInfo={cardCancellationInfo}
              otherInfo={otherInfo}
              policy={policy}
              paymentNetwork={paymentNetwork}
              isTabPanel={true}
              labels={{
                heading: staticT.CC_PRODUCT_PAGE_APR_FEES_TITLE, // APR / Fees
                subHeadingRepayment: // 'Repayment',
                  staticT.CC_PRODUCT_PAGE_APR_FEES_SUB_TITLE,
                subHeadingFees: // 'Fees',
                  staticT.CC_PRODUCT_PAGE_APR_FEES_FEES_LABEL,
                subHeadingCancellation: // 'Card cancellation information',
                  staticT.CC_PRODUCT_PAGE_APR_FEES_CANCELLATION_LABEL,
                subHeadingOther: // 'Other information',
                  staticT.CC_PRODUCT_PAGE_APR_FEES_OTHER_LABEL,
                toggleLabels: {
                  showMore: staticT.showMoreLabel,
                  showLess: staticT.showLessLabel,
                },
                showMoreThreshold: {
                  fees: 4, // TODO store somewhere
                  repayment: 4, // TODO store somewhere
                  cancellation: 4, // TODO store somewhere
                  otherInfo: 4, // TODO store somewhere
                },
                otherInfoLabels: {
                  paymentNetwork: // 'Credit card type',
                    staticT.CC_PRODUCT_PAGE_APR_FEES_PAYMENT_NETWORK_LABEL,
                  supportedPaymentTypes: // 'Supported Payment Types',
                    staticT.CC_PRODUCT_PAGE_APR_FEES_SUPPORTED_PAYMENT_TYPES_LABEL,
                },
              }}
            />
          )}
        </AnchorTabs>
        {categories && categories.length > 0 && (
          <CompareOtherProductsPanel
            categories={categories}
            categoryRouteTemplate={categoryRouteTemplate}
            language={language}
            vertical={vertical}
            labels={{
              title: // 'Isn\'t the best product for you?',
                staticT.CC_PRODUCT_PAGE_COMPARE_TITLE,
              subtitle: // 'Compare more credit cards on MoneyHero.com.hk',
                staticT.CC_PRODUCT_PAGE_COMPARE_SUB_TITLE,
              compare: // 'Compare credit card',
                staticT.CC_PRODUCT_PAGE_COMPARE_COMPARE_LABEL,
            }}
          />
        )}
        { seoTable && <SeoTable {...seoTable} language={language} /> }
        {generalFaq && (
          <Freeze>
            <FaqPanel
              {...generalFaq}
              desktopImage={generalFaq.desktopImage && {
                ...generalFaq.desktopImage,
                alt: generalFaq.desktopImage.filename,
              }}
              mobileImage={generalFaq.mobileImage && {
                ...generalFaq.mobileImage,
                alt: generalFaq.mobileImage.filename,
              }}
            />
          </Freeze>
        )}
        {_.get(product, 'ctaButton.show') && (
          <StickyCTABanner
            name={product.productName}
            ctaButton={ctaButton}
            image={product.image || {}}
            hidden={ctaBannerHidden}
            onCtaLinkClick={() => this.onApplyButtonClick('stickyApply')}
          />
        )}
      </div>
    );
  }
}

ProductPage.defaultProps = {
  product: {},
  categories: null,
  categoryRouteTemplate: null,
  language: null,
  locale: null,
  staticPageText: null,
  seoTable: null,
  vertical: '',
  shortDescription: [],
  policy: [],
  paymentNetwork: [],
  subscribedContainers: [],
  location: {},
  cookies: {},
  track: null,
};

ProductPage.propTypes = {
  product: shape({}),
  categories: arrayOf(shape({})),
  categoryRouteTemplate: shape([]),
  language: string,
  locale: string,
  staticPageText: shape([]),
  seoTable: shape({}),
  vertical: string,
  shortDescription: arrayOf(string),
  policy: arrayOf(shape({})),
  paymentNetwork: arrayOf(shape({ key: string, display: string })),
  subscribedContainers: arrayOf(shape({})),
  location: shape({}),
  cookies: shape({}),
  track: func,
};

export default withSubscribe(withTracking(withRouter(ProductPage)))([SupportButtonStateContainer]);
